/**
 * Business Account Portal primary rail — 07-03 eight sections, NAV-2 / NAV-6.
 */

export type BusinessNavIcon =
  | 'overview'
  | 'approvals'
  | 'people'
  | 'policies'
  | 'budgets'
  | 'invoices'
  | 'reports'
  | 'settings'
  | 'alerts';

export type BusinessNavChild = {
  href: string;
  label: string;
  screenId: string;
  /** Shown when any listed permission is granted. Omit = always when authed. */
  anyOf?: readonly string[];
  /** Extra path prefixes that keep this child (and its parent) current. */
  matchHrefs?: readonly string[];
};

export type BusinessNavItem = {
  href: string;
  label: string;
  screenId: string;
  icon: BusinessNavIcon;
  /** NAV-2 — Approvals is promoted on the rail. */
  promoted?: boolean;
  /** Shown when any listed permission is granted. Omit = always when authed. */
  anyOf?: readonly string[];
  children?: readonly BusinessNavChild[];
  matchHrefs?: readonly string[];
};

const PEOPLE_CHILDREN: readonly BusinessNavChild[] = [
  {
    href: '/employee-list',
    label: 'Employees',
    screenId: 'SCR-BIZ-009',
    anyOf: ['PERM-corporate.employee.read', 'PERM-corporate.employee.invite'],
    matchHrefs: ['/employee-detail'],
  },
  {
    href: '/invite-employees',
    label: 'Invite',
    screenId: 'SCR-BIZ-010',
    anyOf: ['PERM-corporate.employee.invite'],
  },
  {
    href: '/bulk-employee-import',
    label: 'Bulk import',
    screenId: 'SCR-BIZ-012',
    anyOf: ['PERM-corporate.employee.invite'],
  },
  {
    href: '/department-list',
    label: 'Departments',
    screenId: 'SCR-BIZ-005',
    anyOf: ['PERM-corporate.department.read', 'PERM-corporate.department.manage'],
    matchHrefs: ['/department-editor'],
  },
  {
    href: '/hierarchy-view',
    label: 'Hierarchy',
    screenId: 'SCR-BIZ-008',
    anyOf: ['PERM-corporate.department.read', 'PERM-corporate.department.manage'],
  },
  {
    href: '/cost-centre-list',
    label: 'Cost centres',
    screenId: 'SCR-BIZ-007',
    anyOf: ['PERM-corporate.costcentre.manage'],
  },
];

const REPORTS_CHILDREN: readonly BusinessNavChild[] = [
  {
    href: '/spend-report',
    label: 'Spend report',
    screenId: 'SCR-BIZ-026',
    anyOf: ['PERM-report.corporate.read'],
  },
  {
    href: '/export-centre',
    label: 'Export centre',
    screenId: 'SCR-BIZ-027',
    anyOf: ['PERM-report.export.execute', 'PERM-report.corporate.export'],
  },
];

const SETTINGS_CHILDREN: readonly BusinessNavChild[] = [
  {
    href: '/account-settings',
    label: 'Account',
    screenId: 'SCR-BIZ-028',
    anyOf: ['PERM-corporate.account.update', 'PERM-corporate.billing.update'],
  },
  {
    href: '/approval-chain',
    label: 'Approval chain',
    screenId: 'SCR-BIZ-016',
    anyOf: ['PERM-approval.chain.update'],
    matchHrefs: ['/delegation-settings'],
  },
  {
    href: '/corporate-api-clients',
    label: 'API clients',
    screenId: 'SCR-BIZ-029',
    anyOf: [
      'PERM-identity.apiclient.create',
      'PERM-identity.apiclient.revoke',
      'PERM-identity.apiclient.read',
    ],
  },
  {
    href: '/account-activity',
    label: 'Activity',
    screenId: 'SCR-BIZ-030',
    anyOf: ['PERM-audit.log.read'],
  },
];

/** Architecture primary rail, in 07-03 order. Not on the rail: Setup, Organisation, Exports, Alerts. */
export const BUSINESS_ROOT_NAV: readonly BusinessNavItem[] = [
  {
    href: '/account-overview',
    label: 'Overview',
    screenId: 'SCR-BIZ-004',
    icon: 'overview',
  },
  {
    href: '/approval-queue',
    label: 'Approvals',
    screenId: 'SCR-BIZ-018',
    icon: 'approvals',
    promoted: true,
    anyOf: ['PERM-approval.request.read', 'PERM-approval.request.decide'],
    matchHrefs: ['/approval-detail', '/approvals'],
  },
  {
    href: '/employee-list',
    label: 'People',
    screenId: 'SCR-BIZ-009',
    icon: 'people',
    anyOf: [
      'PERM-corporate.employee.read',
      'PERM-corporate.employee.invite',
      'PERM-corporate.department.read',
      'PERM-corporate.department.manage',
      'PERM-corporate.costcentre.manage',
    ],
    children: PEOPLE_CHILDREN,
    matchHrefs: ['/employee-detail', '/department-editor'],
  },
  {
    href: '/policy-list',
    label: 'Policies',
    screenId: 'SCR-BIZ-013',
    icon: 'policies',
    anyOf: ['PERM-corporate.policy.read', 'PERM-corporate.policy.update'],
    matchHrefs: ['/policy-editor', '/policy-simulator'],
  },
  {
    href: '/budget-monitor',
    label: 'Budgets',
    screenId: 'SCR-BIZ-020',
    icon: 'budgets',
    anyOf: ['PERM-corporate.spend.read', 'PERM-corporate.budget.update'],
    matchHrefs: ['/budget-alerts'],
  },
  {
    href: '/invoice-list',
    label: 'Invoices',
    screenId: 'SCR-BIZ-022',
    icon: 'invoices',
    anyOf: ['PERM-invoice.invoice.list', 'PERM-invoice.invoice.read'],
    matchHrefs: ['/invoice-detail', '/invoice-query', '/payment-record', '/invoices'],
  },
  {
    href: '/spend-report',
    label: 'Reports',
    screenId: 'SCR-BIZ-026',
    icon: 'reports',
    anyOf: [
      'PERM-report.corporate.read',
      'PERM-report.export.execute',
      'PERM-report.corporate.export',
    ],
    children: REPORTS_CHILDREN,
  },
  {
    href: '/account-settings',
    label: 'Settings',
    screenId: 'SCR-BIZ-028',
    icon: 'settings',
    anyOf: [
      'PERM-corporate.account.update',
      'PERM-corporate.billing.update',
      'PERM-approval.chain.update',
      'PERM-identity.apiclient.create',
      'PERM-identity.apiclient.revoke',
      'PERM-identity.apiclient.read',
      'PERM-audit.log.read',
    ],
    children: SETTINGS_CHILDREN,
    matchHrefs: ['/delegation-settings'],
  },
];

export const BUSINESS_RAIL_LABELS = BUSINESS_ROOT_NAV.map((item) => item.label);

export const BUSINESS_RAIL_EXCLUDED_LABELS = [
  'Setup',
  'Organisation',
  'Exports',
  'Alerts',
] as const;

function permVisible(
  anyOf: readonly string[] | undefined,
  can: (perm: string) => boolean,
  permissionsReady: boolean,
): boolean {
  if (!anyOf?.length) return true;
  if (!permissionsReady) return false;
  return anyOf.some((p) => can(p));
}

function withRetargetedHref(item: BusinessNavItem, children: BusinessNavChild[]): BusinessNavItem {
  if (!children.length) return { ...item, children: undefined };
  const hrefStillReachable = children.some((child) => child.href === item.href);
  return {
    ...item,
    href: hrefStillReachable ? item.href : children[0]!.href,
    children,
  };
}

/** NAV-6 — omit sections (and children) the role can never use; do not disable. */
export function filterBusinessNav(
  items: readonly BusinessNavItem[],
  can: (perm: string) => boolean,
  permissionsReady: boolean,
): BusinessNavItem[] {
  return items.flatMap((item) => {
    const children = (item.children ?? []).filter((child) =>
      permVisible(child.anyOf, can, permissionsReady),
    );
    const selfVisible = permVisible(item.anyOf, can, permissionsReady);
    if (!selfVisible && children.length === 0) return [];
    if (!selfVisible && children.length > 0) {
      return [withRetargetedHref(item, children)];
    }
    return [withRetargetedHref(item, children)];
  });
}

/** Strip optional `/a/:accountSlug` prefix so unprefixed and scoped URLs share IA. */
export function stripAccountScopePath(pathname: string): string {
  const match = pathname.match(/^\/a\/[^/]+(\/.*)?$/);
  if (!match) return pathname;
  return match[1] && match[1].length > 0 ? match[1] : '/';
}

function pathMatchesHref(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function childIsCurrent(pathname: string, child: BusinessNavChild): boolean {
  if (pathMatchesHref(pathname, child.href)) return true;
  return (child.matchHrefs ?? []).some((prefix) => pathMatchesHref(pathname, prefix));
}

export function isBusinessNavItemCurrent(item: BusinessNavItem, pathname: string): boolean {
  const path = stripAccountScopePath(pathname);
  if (item.href === '/account-overview') {
    return path === '/account-overview';
  }
  if (pathMatchesHref(path, item.href)) return true;
  if ((item.matchHrefs ?? []).some((prefix) => pathMatchesHref(path, prefix))) return true;
  return (item.children ?? []).some((child) => childIsCurrent(path, child));
}

export function isBusinessNavChildCurrent(child: BusinessNavChild, pathname: string): boolean {
  return childIsCurrent(stripAccountScopePath(pathname), child);
}

export function activeBusinessNavItem(
  items: readonly BusinessNavItem[],
  pathname: string,
): BusinessNavItem | undefined {
  return items.find((item) => isBusinessNavItemCurrent(item, pathname));
}
